import { create } from 'zustand';
import { NotificationItem, getNotificationFeed, NotificationFeedRequest } from 'features/notifications/services/notification-service';

import { useConfigStore } from 'state/store/config/useConfigStore';

interface NotificationFeedStoreState {
  notificationList: NotificationItem[];
  unseenCount: number;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  unseenNotificationIds: string[];
  page: number;
  // Actions
  setNotificationList: (notifications: NotificationItem[], unseenCount?: number) => void;
  setUnseenCount: (count: number) => void;
  fetchAndSetNotification: (page: number, pageSize?: number, append?: boolean) => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  resetPagination: () => void;
  getUnseenNotificationIds: () => string[];
  // getUnseenNotifications: () => NotificationItem[];
}

const initialState = {
  notificationList: [],
  unseenCount: 0,
  totalCount: 0,
  currentPage: 0,
  pageSize: 10,
  page: 0,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  unseenNotificationIds: [],
};

export const useNotificationFeedStore = create<NotificationFeedStoreState>((set, get) => ({
  ...initialState,
  isLoading: false,
  setNotificationList: (notifications, unseenCount) => {
    const unseenNotifications = notifications.filter((n) => !n.seen);
    const unseenIds = unseenNotifications.map((n) => n.itemId || n.id);
    
    set({
      notificationList: notifications,
      unseenCount: unseenCount ?? get().unseenCount,
      unseenNotificationIds: unseenIds,
    }); 
  },

  setUnseenCount: (count) => {
    set({ unseenCount: count });
  },

  resetPagination: () => {
    set({
      notificationList: [],
      currentPage: 0,
      totalCount: 0,
      hasMore: false,
    });
  },

  fetchAndSetNotification: async (page: number, pageSize?: number, append = false) => {
    const state = get();
    const notificationList = state.notificationList;
    // Use provided pageSize or fall back to state's pageSize
    const effectivePageSize = pageSize ?? state.pageSize;
    const currentPage = page ?? state.page;
    
    // Set loading state
    if (append) {
      set({ isLoadingMore: true });
    } else {
      if (notificationList.length > 0) {
        set({ isLoading: false });
      } else {
        set({ isLoading: true });
      }
    }

    try {
      const configurationSettings = useConfigStore.getState().configurationSettings;
      
      if (!configurationSettings?.blocksKey) {
        throw new Error('Configuration settings or blocksKey is not available');
      }

      const request: NotificationFeedRequest = {
        page: currentPage,
        pageSize: effectivePageSize,
        filter: null,
        sort: null,
        projectKey: configurationSettings.blocksKey,
      };

      const response = await getNotificationFeed(configurationSettings, request);
      
      const newNotifications = response.data || [];
      const totalCount = response.totalCount || 0;
      const unseenCount = response.unseenCount || 0;
      
      const updatedNotifications = append 
        ? [...notificationList, ...newNotifications]
        : newNotifications;
      
      // Calculate unseen IDs from all notifications
      const unseenNotifications = updatedNotifications.filter((n) => !n.seen);
      const unseenIds = unseenNotifications.map((n) => n.itemId || n.id);
      
      // Calculate if there are more notifications to load
      const hasMore = updatedNotifications.length < totalCount;
      
      set({
        notificationList: updatedNotifications,
        unseenCount,
        totalCount,
        currentPage: currentPage,
        pageSize: effectivePageSize,
        hasMore,
        unseenNotificationIds: unseenIds,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching notifications:', error);
      throw error;
    } finally {
      set({ isLoading: false, isLoadingMore: false });
    }
  },

  loadMoreNotifications: async () => {
    const state = get();
    if (state.isLoadingMore || !state.hasMore) {
      return;
    }

    const nextPage = state.currentPage + 1;
    await get().fetchAndSetNotification(nextPage, state.pageSize, true);
  },

  goToPage: async (page: number) => {
    const state = get();
    if (state.isLoading || state.isLoadingMore || page === state.currentPage) {
      return;
    }

    await get().fetchAndSetNotification(page, state.pageSize, false);
  },

  getUnseenNotificationIds: () => {
    const state = get();
    return state.unseenNotificationIds;
  },

}));

